import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Calendar, 
  Settings, 
  LogOut, 
  Plus, 
  Edit, 
  Trash2, 
  Home,
  Save,
  X,
  Users,
  UserPlus,
  Key,
  MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const AdminDashboard = () => {
  const { token, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  
  // Event form state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '',
    description_de: '',
    description_en: '',
    month: 1,
    day: 1,
    year: '',
    end_day: '',
    end_month: '',
    end_year: '',
    color_primary: '#00F0FF',
    color_secondary: '#020408',
    image_url: ''
  });

  // Admin form state
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({
    username: '',
    password: ''
  });

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTargetAdmin, setPasswordTargetAdmin] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Comment form state
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentForm, setCommentForm] = useState({
    name: '',
    message: ''
  });

  // Get current admin info
  const [currentAdminId, setCurrentAdminId] = useState(null);

  // Auth headers
  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    try {
      const [eventsRes, settingsRes, adminsRes, meRes, commentsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admins`, authHeaders),
        axios.get(`${API}/auth/me`, authHeaders),
        axios.get(`${API}/comments`)
      ]);
      setEvents(eventsRes.data);
      setSettings(settingsRes.data);
      setAdmins(adminsRes.data);
      setCurrentAdminId(meRes.data.id);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  // Event CRUD
  const openEventDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        name: event.name,
        description_de: event.description_de || '',
        description_en: event.description_en || event.description || '',
        month: event.month,
        day: event.day,
        year: event.year || '',
        end_day: event.end_day || '',
        end_month: event.end_month || '',
        end_year: event.end_year || '',
        color_primary: event.color_primary,
        color_secondary: event.color_secondary,
        image_url: event.image_url || ''
      });
    } else {
      setEditingEvent(null);
      setEventForm({
        name: '',
        description_de: '',
        description_en: '',
        month: 1,
        day: 1,
        year: '',
        end_day: '',
        end_month: '',
        end_year: '',
        color_primary: '#00F0FF',
        color_secondary: '#020408',
        image_url: ''
      });
    }
    setEventDialogOpen(true);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...eventForm,
        month: parseInt(eventForm.month),
        day: parseInt(eventForm.day),
        year: eventForm.year ? parseInt(eventForm.year) : null,
        end_day: eventForm.end_day ? parseInt(eventForm.end_day) : null,
        end_month: eventForm.end_month ? parseInt(eventForm.end_month) : null,
        end_year: eventForm.end_year ? parseInt(eventForm.end_year) : null
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, authHeaders);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload, authHeaders);
        toast.success('Event created');
      }
      setEventDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`, authHeaders);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Admin CRUD
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admins`, adminForm, authHeaders);
      toast.success('Admin created');
      setAdminDialogOpen(false);
      setAdminForm({ username: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    
    try {
      await axios.delete(`${API}/admins/${adminId}`, authHeaders);
      toast.success('Admin deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  // Password change
  const openPasswordDialog = (admin) => {
    setPasswordTargetAdmin(admin);
    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.put(`${API}/admins/${passwordTargetAdmin.id}/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, authHeaders);
      toast.success(`Password changed for ${passwordTargetAdmin.username}`);
      setPasswordDialogOpen(false);
      setPasswordTargetAdmin(null);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // Settings update
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, settings, authHeaders);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // Comment CRUD
  const handleCreateComment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/comments`, {
        ...commentForm,
        is_admin: true
      }, authHeaders);
      toast.success('Kommentar erstellt');
      setCommentDialogOpen(false);
      setCommentForm({ name: '', message: '' });
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Erstellen des Kommentars');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Möchtest du diesen Kommentar wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/comments/${commentId}`, authHeaders);
      toast.success('Kommentar gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen des Kommentars');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-cyan-electric font-mono text-xl animate-pulse">
          LOADING COMMAND CENTER...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void grid-bg">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 admin-sidebar p-4 flex flex-col">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 border-2 border-cyan-electric rounded flex items-center justify-center">
              <span className="text-cyan-electric font-rajdhani font-bold">SC</span>
            </div>
            <div>
              <h1 className="font-rajdhani text-lg font-bold uppercase tracking-wider text-white">
                Admin Panel
              </h1>
              <p className="font-mono text-xs text-cyan-electric/50">Command Center</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-2">
          <button
            onClick={() => setActiveTab('events')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all ${
              activeTab === 'events' 
                ? 'bg-cyan-electric/10 border border-cyan-electric/30 text-cyan-electric' 
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid="nav-events"
          >
            <Calendar className="w-5 h-5" />
            <span className="font-rajdhani uppercase tracking-wider">Events</span>
          </button>
          
          <button
            onClick={() => setActiveTab('users')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all ${
              activeTab === 'users' 
                ? 'bg-cyan-electric/10 border border-cyan-electric/30 text-cyan-electric' 
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid="nav-users"
          >
            <Users className="w-5 h-5" />
            <span className="font-rajdhani uppercase tracking-wider">Users</span>
          </button>
          
          <button
            onClick={() => setActiveTab('comments')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all ${
              activeTab === 'comments' 
                ? 'bg-cyan-electric/10 border border-cyan-electric/30 text-cyan-electric' 
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid="nav-comments"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="font-rajdhani uppercase tracking-wider">Kommentare</span>
          </button>
          
          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all ${
              activeTab === 'settings' 
                ? 'bg-cyan-electric/10 border border-cyan-electric/30 text-cyan-electric' 
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="font-rajdhani uppercase tracking-wider">Settings</span>
          </button>
        </nav>

        {/* Bottom Actions */}
        <div className="space-y-2 pt-4 border-t border-cyan-electric/10">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded transition-all"
            data-testid="nav-home"
          >
            <Home className="w-5 h-5" />
            <span className="font-rajdhani uppercase tracking-wider">View Site</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-alert hover:bg-red-alert/10 rounded transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-rajdhani uppercase tracking-wider">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Events Tab */}
        {activeTab === 'events' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-rajdhani text-2xl font-bold uppercase tracking-wider text-white">
                  Event Management
                </h2>
                <p className="font-mono text-sm text-slate-500">{events.length} events registered</p>
              </div>
              
              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => openEventDialog()}
                    className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                    data-testid="add-event-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </Button>
                </DialogTrigger>
                
                <DialogContent className="glass-panel border-cyan-electric/30 max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-rajdhani text-xl uppercase tracking-wider text-cyan-electric">
                      {editingEvent ? 'Edit Event' : 'Create New Event'}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleEventSubmit} className="space-y-4 mt-4" data-testid="event-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Event Name
                        </Label>
                        <Input
                          value={eventForm.name}
                          onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                          className="input-command font-barlow"
                          required
                          data-testid="event-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Start-Monat
                        </Label>
                        <select
                          value={eventForm.month}
                          onChange={(e) => setEventForm({...eventForm, month: e.target.value})}
                          className="w-full h-10 px-3 input-command font-barlow rounded-md"
                          data-testid="event-month-select"
                        >
                          {MONTH_NAMES.map((month, i) => (
                            <option key={i} value={i + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                            Start-Tag
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            max="31"
                            value={eventForm.day}
                            onChange={(e) => setEventForm({...eventForm, day: e.target.value})}
                            className="input-command font-mono"
                            required
                            data-testid="event-day-input"
                          />
                        </div>
                        <div>
                          <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                            Start-Jahr
                          </Label>
                          <Input
                            type="number"
                            min="2020"
                            max="2100"
                            value={eventForm.year}
                            onChange={(e) => setEventForm({...eventForm, year: e.target.value})}
                            className="input-command font-mono"
                            placeholder="Leer = jährlich"
                            data-testid="event-year-input"
                          />
                        </div>
                      </div>
                      
                      {/* END DATE SECTION */}
                      <div className="col-span-2 mt-2">
                        <div className="text-xs font-rajdhani uppercase tracking-wider text-orange-400 mb-2 border-b border-orange-400/20 pb-1">
                          Enddatum (optional - für mehrtägige Events)
                        </div>
                      </div>
                      
                      <div>
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          End-Monat
                        </Label>
                        <select
                          value={eventForm.end_month}
                          onChange={(e) => setEventForm({...eventForm, end_month: e.target.value})}
                          className="w-full h-10 px-3 input-command font-barlow rounded-md"
                          data-testid="event-end-month-select"
                        >
                          <option value="">-- Gleicher Monat --</option>
                          {MONTH_NAMES.map((month, i) => (
                            <option key={i} value={i + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                            End-Tag
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            max="31"
                            value={eventForm.end_day}
                            onChange={(e) => setEventForm({...eventForm, end_day: e.target.value})}
                            className="input-command font-mono"
                            placeholder="Optional"
                            data-testid="event-end-day-input"
                          />
                        </div>
                        <div>
                          <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                            End-Jahr
                          </Label>
                          <Input
                            type="number"
                            min="2020"
                            max="2100"
                            value={eventForm.end_year}
                            onChange={(e) => setEventForm({...eventForm, end_year: e.target.value})}
                            className="input-command font-mono"
                            placeholder="Optional"
                            data-testid="event-end-year-input"
                          />
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Description (Deutsch) 🇩🇪
                        </Label>
                        <Textarea
                          value={eventForm.description_de}
                          onChange={(e) => setEventForm({...eventForm, description_de: e.target.value})}
                          className="input-command font-barlow min-h-[100px]"
                          required
                          data-testid="event-description-de-input"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Description (English) 🇬🇧
                        </Label>
                        <Textarea
                          value={eventForm.description_en}
                          onChange={(e) => setEventForm({...eventForm, description_en: e.target.value})}
                          className="input-command font-barlow min-h-[100px]"
                          required
                          data-testid="event-description-en-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Primary Color
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={eventForm.color_primary}
                            onChange={(e) => setEventForm({...eventForm, color_primary: e.target.value})}
                            className="w-12 h-10 p-1 cursor-pointer"
                            data-testid="event-color-primary"
                          />
                          <Input
                            value={eventForm.color_primary}
                            onChange={(e) => setEventForm({...eventForm, color_primary: e.target.value})}
                            className="input-command font-mono"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Secondary Color
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={eventForm.color_secondary}
                            onChange={(e) => setEventForm({...eventForm, color_secondary: e.target.value})}
                            className="w-12 h-10 p-1 cursor-pointer"
                            data-testid="event-color-secondary"
                          />
                          <Input
                            value={eventForm.color_secondary}
                            onChange={(e) => setEventForm({...eventForm, color_secondary: e.target.value})}
                            className="input-command font-mono"
                          />
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Image URL (Optional)
                        </Label>
                        <Input
                          value={eventForm.image_url}
                          onChange={(e) => setEventForm({...eventForm, image_url: e.target.value})}
                          className="input-command font-mono"
                          placeholder="https://..."
                          data-testid="event-image-input"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEventDialogOpen(false)}
                        className="border-slate-600 text-slate-400 hover:bg-slate-800"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                        data-testid="event-submit-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {editingEvent ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Events Table */}
            <div className="glass-panel holo-border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-cyan-electric/20 hover:bg-transparent">
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">Event</TableHead>
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">Date</TableHead>
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">Colors</TableHead>
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <TableRow 
                      key={event.id} 
                      className="border-cyan-electric/10 hover:bg-cyan-electric/5 table-row-scan"
                      data-testid={`event-row-${event.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-rajdhani font-semibold text-white">{event.name}</p>
                          <p className="font-mono text-xs text-slate-500 truncate max-w-xs">
                            {(event.description_de || event.description_en || event.description || '').substring(0, 50)}...
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-slate-400">
                        {event.day}. {MONTH_NAMES[event.month - 1]}{event.year ? ` ${event.year}` : ''}
                        {(event.end_day || event.end_month) && (
                          <>
                            {' - '}
                            {event.end_day || event.day}. {MONTH_NAMES[(event.end_month || event.month) - 1]}
                            {event.end_year ? ` ${event.end_year}` : (event.year ? ` ${event.year}` : '')}
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <div 
                            className="w-6 h-6 rounded border border-white/20"
                            style={{ backgroundColor: event.color_primary }}
                          />
                          <div 
                            className="w-6 h-6 rounded border border-white/20"
                            style={{ backgroundColor: event.color_secondary }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEventDialog(event)}
                            className="text-cyan-electric hover:bg-cyan-electric/10"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.id)}
                            className="text-red-alert hover:bg-red-alert/10"
                            data-testid={`delete-event-${event.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-rajdhani text-2xl font-bold uppercase tracking-wider text-white">
                  User Management
                </h2>
                <p className="font-mono text-sm text-slate-500">{admins.length} admin(s) registered</p>
              </div>
              
              <div className="flex gap-3">
                {/* Add Admin Dialog */}
                <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                      data-testid="add-admin-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Admin
                    </Button>
                  </DialogTrigger>
                  
                  <DialogContent className="glass-panel border-cyan-electric/30 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-rajdhani text-xl uppercase tracking-wider text-cyan-electric">
                        Create New Admin
                      </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleCreateAdmin} className="space-y-4 mt-4" data-testid="admin-form">
                      <div>
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Username
                        </Label>
                        <Input
                          value={adminForm.username}
                          onChange={(e) => setAdminForm({...adminForm, username: e.target.value})}
                          className="input-command font-mono"
                          required
                          data-testid="admin-username-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                          Password
                        </Label>
                        <Input
                          type="password"
                          value={adminForm.password}
                          onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                          className="input-command font-mono"
                          required
                          minLength={6}
                          data-testid="admin-password-input"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setAdminDialogOpen(false)}
                          className="border-slate-600 text-slate-400 hover:bg-slate-800"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                          data-testid="admin-submit-btn"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Create Admin
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Password Change Dialog */}
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogContent className="glass-panel border-cyan-electric/30 max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-rajdhani text-xl uppercase tracking-wider text-cyan-electric">
                    Change Password for {passwordTargetAdmin?.username}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleChangePassword} className="space-y-4 mt-4" data-testid="password-form">
                  {/* Only show current password field if changing own password */}
                  {passwordTargetAdmin?.id === currentAdminId && (
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Current Password
                      </Label>
                      <Input
                        type="password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                        className="input-command font-mono"
                        required
                        data-testid="current-password-input"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                      New Password
                    </Label>
                    <Input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      className="input-command font-mono"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                      Confirm New Password
                    </Label>
                    <Input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      className="input-command font-mono"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPasswordDialogOpen(false)}
                      className="border-slate-600 text-slate-400 hover:bg-slate-800"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="btn-power bg-gold-elite/10 border border-gold-elite/50 text-gold-elite hover:bg-gold-elite/20"
                      data-testid="password-submit-btn"
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Change Password
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Admins Table */}
            <div className="glass-panel holo-border rounded-lg overflow-hidden max-w-2xl">
              <Table>
                <TableHeader>
                  <TableRow className="border-cyan-electric/20 hover:bg-transparent">
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">Username</TableHead>
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">ID</TableHead>
                    <TableHead className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow 
                      key={admin.id} 
                      className="border-cyan-electric/10 hover:bg-cyan-electric/5"
                      data-testid={`admin-row-${admin.id}`}
                    >
                      <TableCell className="font-rajdhani font-semibold text-white">
                        {admin.username}
                        {admin.id === currentAdminId && (
                          <span className="ml-2 text-xs text-cyan-electric/50">(you)</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {admin.id.substring(0, 8)}...
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPasswordDialog(admin)}
                            className="text-gold-elite hover:bg-gold-elite/10"
                            data-testid={`change-password-${admin.id}`}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAdmin(admin.id)}
                            className="text-red-alert hover:bg-red-alert/10"
                            data-testid={`delete-admin-${admin.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-rajdhani text-2xl font-bold uppercase tracking-wider text-white">
                  Kommentar-Verwaltung
                </h2>
                <p className="font-mono text-sm text-slate-500">{comments.length} Kommentare</p>
              </div>
              
              <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                    data-testid="add-comment-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Kommentar hinzufügen
                  </Button>
                </DialogTrigger>
                
                <DialogContent className="glass-panel border-cyan-electric/30 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-rajdhani text-xl uppercase tracking-wider text-cyan-electric">
                      Neuen Kommentar erstellen
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleCreateComment} className="space-y-4 mt-4" data-testid="comment-form">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Name
                      </Label>
                      <Input
                        value={commentForm.name}
                        onChange={(e) => setCommentForm({...commentForm, name: e.target.value})}
                        className="input-command font-barlow"
                        required
                        data-testid="comment-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Nachricht
                      </Label>
                      <Textarea
                        value={commentForm.message}
                        onChange={(e) => setCommentForm({...commentForm, message: e.target.value})}
                        className="input-command font-barlow min-h-[100px]"
                        required
                        data-testid="comment-message-input"
                      />
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCommentDialogOpen(false)}
                        className="border-slate-600 text-slate-400 hover:bg-slate-800"
                      >
                        Abbrechen
                      </Button>
                      <Button
                        type="submit"
                        className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                        data-testid="comment-submit-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Erstellen
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Comments List */}
            <div className="space-y-4 max-w-4xl">
              {comments.length === 0 ? (
                <div className="glass-panel holo-border rounded-lg p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 font-barlow">Noch keine Kommentare vorhanden</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div 
                    key={comment.id} 
                    className="glass-panel holo-border rounded-lg p-4 table-row-scan"
                    data-testid={`comment-row-${comment.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-rajdhani font-semibold text-white">{comment.name}</span>
                          <span className="font-mono text-xs text-slate-500">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="font-barlow text-slate-300 whitespace-pre-wrap">{comment.message}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-alert hover:bg-red-alert/10 flex-shrink-0"
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div>
            <div className="mb-8">
              <h2 className="font-rajdhani text-2xl font-bold uppercase tracking-wider text-white">
                Site Settings
              </h2>
              <p className="font-mono text-sm text-slate-500">Configure website content</p>
            </div>

            <form onSubmit={handleSettingsSubmit} className="space-y-8 max-w-3xl" data-testid="settings-form">
              {/* Homepage Settings */}
              <div className="glass-panel holo-border rounded-lg p-6">
                <h3 className="font-rajdhani text-lg uppercase tracking-wider text-cyan-electric mb-4">
                  Homepage
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                      Site Title
                    </Label>
                    <Input
                      value={settings.site_title || ''}
                      onChange={(e) => setSettings({...settings, site_title: e.target.value})}
                      className="input-command font-barlow"
                      data-testid="settings-title-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Slogan 🇩🇪
                      </Label>
                      <Textarea
                        value={settings.hero_subtitle_de || ''}
                        onChange={(e) => setSettings({...settings, hero_subtitle_de: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-slogan-de"
                      />
                    </div>
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Slogan 🇬🇧
                      </Label>
                      <Textarea
                        value={settings.hero_subtitle_en || ''}
                        onChange={(e) => setSettings({...settings, hero_subtitle_en: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-slogan-en"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Footer 🇩🇪
                      </Label>
                      <Input
                        value={settings.footer_text_de || ''}
                        onChange={(e) => setSettings({...settings, footer_text_de: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-footer-de"
                      />
                    </div>
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Footer 🇬🇧
                      </Label>
                      <Input
                        value={settings.footer_text_en || ''}
                        onChange={(e) => setSettings({...settings, footer_text_en: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-footer-en"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                      Background Image URL
                    </Label>
                    <Input
                      value={settings.background_image || ''}
                      onChange={(e) => setSettings({...settings, background_image: e.target.value})}
                      className="input-command font-mono"
                      placeholder="https://..."
                      data-testid="settings-bg-input"
                    />
                  </div>
                </div>
              </div>

              {/* Login Page Settings */}
              <div className="glass-panel holo-border rounded-lg p-6">
                <h3 className="font-rajdhani text-lg uppercase tracking-wider text-cyan-electric mb-4">
                  Login Page
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Login Title 🇩🇪
                      </Label>
                      <Input
                        value={settings.login_title_de || ''}
                        onChange={(e) => setSettings({...settings, login_title_de: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-title-de"
                      />
                    </div>
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Login Title 🇬🇧
                      </Label>
                      <Input
                        value={settings.login_title_en || ''}
                        onChange={(e) => setSettings({...settings, login_title_en: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-title-en"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Login Subtitle 🇩🇪
                      </Label>
                      <Input
                        value={settings.login_subtitle_de || ''}
                        onChange={(e) => setSettings({...settings, login_subtitle_de: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-subtitle-de"
                      />
                    </div>
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Login Subtitle 🇬🇧
                      </Label>
                      <Input
                        value={settings.login_subtitle_en || ''}
                        onChange={(e) => setSettings({...settings, login_subtitle_en: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-subtitle-en"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Protocol Text 🇩🇪
                      </Label>
                      <Input
                        value={settings.login_protocol_text_de || ''}
                        onChange={(e) => setSettings({...settings, login_protocol_text_de: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-protocol-de"
                      />
                    </div>
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Protocol Text 🇬🇧
                      </Label>
                      <Input
                        value={settings.login_protocol_text_en || ''}
                        onChange={(e) => setSettings({...settings, login_protocol_text_en: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-protocol-en"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Login Button 🇩🇪
                      </Label>
                      <Input
                        value={settings.login_button_text_de || ''}
                        onChange={(e) => setSettings({...settings, login_button_text_de: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-button-de"
                      />
                    </div>
                    <div>
                      <Label className="font-rajdhani text-xs uppercase tracking-wider text-cyan-electric/70">
                        Login Button 🇬🇧
                      </Label>
                      <Input
                        value={settings.login_button_text_en || ''}
                        onChange={(e) => setSettings({...settings, login_button_text_en: e.target.value})}
                        className="input-command font-barlow"
                        data-testid="settings-login-button-en"
                      />
                    </div>
                  </div>
                </div>
              </div>
                
              <Button
                type="submit"
                className="btn-power bg-cyan-electric/10 border border-cyan-electric/50 text-cyan-electric hover:bg-cyan-electric/20"
                data-testid="settings-submit-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save All Settings
              </Button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;

